import {
	Component,
	forwardRef,
	EventEmitter,
	Output,
	Input,
} from '@angular/core';
import { CustomerLink, ServiceType } from 'src/shared/api';
import {
	idComparer,
	touchControl,
	displayDescription,
} from 'src/shared/utilities/utility-functions';
import { SelfDestruct } from 'src/shared/utilities/self-destruct';
import {
	ControlValueAccessor,
	FormControl,
	NG_VALUE_ACCESSOR,
} from '@angular/forms';
import {
	takeUntil,
	map,
	filter,
	distinctUntilChanged,
	skipWhile,
	first,
} from 'rxjs/operators';
import { LookUpsService } from 'src/shared/lookups.service';
import { coerceBooleanProperty } from '@angular/cdk/coercion';

@Component({
	selector: 'rpshared-service-type',
	templateUrl: './service-type.component.html',
	providers: [
		{
			provide: NG_VALUE_ACCESSOR,
			useExisting: forwardRef(() => ServiceTypeComponent),
			multi: true,
		},
	],
})
export class ServiceTypeComponent
	extends SelfDestruct
	implements ControlValueAccessor
{
	private _required = false;
	@Input()
	get required(): boolean {
		return this._required;
	}
	set required(value: boolean) {
		this._required = coerceBooleanProperty(value);
	}

	private _readonly = false;
	@Input()
	get readonly(): boolean {
		return this._readonly;
	}
	set readonly(value: boolean) {
		this._readonly = coerceBooleanProperty(value);
	}

	@Output() optionSelected = new EventEmitter<ServiceType>();

	serviceType = new FormControl('') as FormControl;
	initialValue: ServiceType;

	readonly serviceTypes$ = this.lookUpsService.serviceTypes;
	readonly displayDescription = displayDescription;
	readonly idComparer = idComparer;

	constructor(private readonly lookUpsService: LookUpsService) {
		super();
	}

	// model --> view
	writeValue(value: ServiceType | number): void {
		if (typeof value === 'number') {
			this.lookUpsService.serviceTypes
				.pipe(
					first(),
					map((serviceTypes) =>
						serviceTypes.find((rr) => rr.id === value)
					)
				)
				.subscribe((serviceType) => {
					this.initialValue = serviceType;
					this.serviceType.setValue(serviceType, {
						emitEvent: false,
					});
				});
		} else if (value) {
			this.initialValue = value;
			this.serviceType.setValue(value, { emitEvent: false });
		} else {
			this.serviceType.reset('', { emitEvent: false });
		}
	}

	// view --> model
	registerOnChange(fn: (value: ServiceType | number) => void) {
		this.serviceType.valueChanges
			.pipe(takeUntil(this.destroyed))
			.subscribe(fn);
	}

	registerOnTouched(fn: () => void) {
		this.onTouched = touchControl(this.serviceType)(fn);
	}

	setDisabledState(isDisabled: boolean) {
		if (isDisabled) {
			this.serviceType.disable({ emitEvent: false });
		} else {
			this.serviceType.enable({ emitEvent: false });
		}
	}

	onTouched: () => void = () => {};
}

@Component({
	selector: 'rpshared-service-type-id',
	templateUrl: './service-type.component.html',
	providers: [
		{
			provide: NG_VALUE_ACCESSOR,
			useExisting: forwardRef(() => ServiceTypeIdComponent),
			multi: true,
		},
	],
})
export class ServiceTypeIdComponent extends ServiceTypeComponent {
	registerOnChange(fn: (value: CustomerLink | number) => void) {
		this.serviceType.valueChanges
			.pipe(
				filter((v) => !v || typeof v !== 'string'),
				map((v) => (v ? v.id : null)),
				distinctUntilChanged(),
				skipWhile<number | null>(
					(v) => v === undefined && !this.initialValue
				),
				takeUntil(this.destroyed)
			)
			.subscribe(fn);
	}
}
